import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, KeyRound, ShieldCheck, User } from "lucide-react";
import { useAuth } from "@/AuthContext";

export default function Users() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ username: "", email: "", password: "", role: "staff", allowed_client_ids: [] });
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [pwNew, setPwNew] = useState("");

  const load = async () => {
    const [u, c] = await Promise.all([api.get("/users"), api.get("/clients")]);
    setUsers(u.data);
    setClients(c.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ username: "", email: "", password: "", role: "staff", allowed_client_ids: [] }); setOpenForm(true); };
  const openEdit = (u) => { setEditing(u); setForm({ username: u.username, email: u.email, password: "", role: u.role, allowed_client_ids: u.allowed_client_ids || [] }); setOpenForm(true); };

  const toggleClient = (cid) => {
    const s = new Set(form.allowed_client_ids);
    if (s.has(cid)) s.delete(cid); else s.add(cid);
    setForm({ ...form, allowed_client_ids: Array.from(s) });
  };

  const submit = async () => {
    if (!editing && (!form.username.trim() || !form.email.trim() || !form.password)) return toast.error("Username, email, password required");
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { email: form.email, role: form.role, allowed_client_ids: form.allowed_client_ids });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setOpenForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const openPw = (u) => { setPwUser(u); setPwNew(""); setPwOpen(true); };
  const savePw = async () => {
    if (pwNew.length < 6) return toast.error("Password must be at least 6 characters");
    try {
      await api.put(`/users/${pwUser.id}/password`, { password: pwNew });
      toast.success("Password reset");
      setPwOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || id;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">Administration</div>
          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl mt-2 tracking-tight">User Access</h1>
          <p className="text-slate-400 text-sm mt-2">Create logins for your team and control which clients each person can view.</p>
        </div>
        <Dialog open={openForm} onOpenChange={setOpenForm}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="btn-add-user" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold">
              <UserPlus size={16} className="mr-2" /> Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0E1524] border-slate-800 text-white max-w-lg">
            <DialogHeader><DialogTitle className="font-heading">{editing ? `Edit ${editing.username}` : "New User"}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-caps !text-slate-400">Username *</Label>
                  <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} disabled={!!editing} placeholder="e.g. john" data-testid="form-user-username" className="mt-1.5" />
                </div>
                <div>
                  <Label className="label-caps !text-slate-400">Email *</Label>
                  <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="john@company.com" data-testid="form-user-email" className="mt-1.5" />
                </div>
              </div>
              {!editing && (
                <div>
                  <Label className="label-caps !text-slate-400">Password *</Label>
                  <Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="min 6 chars" data-testid="form-user-password" className="mt-1.5 font-mono-tabular" />
                </div>
              )}
              <div>
                <Label className="label-caps !text-slate-400">Role</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="form-user-role" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0E1524] border-slate-800 text-white">
                    <SelectItem value="admin" data-testid="role-option-admin">Admin (full access)</SelectItem>
                    <SelectItem value="staff" data-testid="role-option-staff">Staff (limited to selected clients)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.role === "staff" && (
                <div>
                  <Label className="label-caps !text-slate-400">Accessible Clients ({form.allowed_client_ids.length} selected)</Label>
                  <div className="mt-1.5 max-h-60 overflow-y-auto border border-slate-800 rounded-lg divide-y divide-slate-800/60" data-testid="form-user-clients-list">
                    {clients.length === 0 && <div className="p-4 text-sm text-slate-500">No clients yet.</div>}
                    {clients.map(c => (
                      <label key={c.id} className="flex items-center gap-3 p-2.5 cursor-pointer text-sm hover:bg-slate-800/40" data-testid={`user-client-row-${c.id}`}>
                        <Checkbox checked={form.allowed_client_ids.includes(c.id)} onCheckedChange={() => toggleClient(c.id)} data-testid={`user-client-check-${c.id}`} />
                        <span>{c.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenForm(false)} className="border-slate-700 text-slate-800">Cancel</Button>
              <Button onClick={submit} data-testid="form-user-submit" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950">{editing ? "Save" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <div className="card-tactical p-6">
        <div className="overflow-x-auto">
          <table className="tactical" data-testid="table-users">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Client Access</th>
                <th className="!text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} data-testid={`user-row-${u.id}`}>
                  <td className="flex items-center gap-2 font-semibold">
                    {u.role === "admin" ? <ShieldCheck size={14} className="text-amber-400" /> : <User size={14} className="text-slate-400" />}
                    {u.username}
                    {u.id === me?.id && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400">YOU</span>}
                  </td>
                  <td className="text-slate-300">{u.email}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${u.role === "admin" ? "bg-amber-500/15 text-amber-400" : "bg-slate-500/15 text-slate-300"}`}>{u.role.toUpperCase()}</span>
                  </td>
                  <td className="text-slate-300 text-xs">
                    {u.role === "admin" ? <span className="text-emerald-400">All clients</span> : (
                      (u.allowed_client_ids || []).length === 0
                        ? <span className="text-rose-400">None</span>
                        : <span>{(u.allowed_client_ids || []).slice(0, 3).map(clientName).join(", ")}{(u.allowed_client_ids || []).length > 3 ? ` +${(u.allowed_client_ids || []).length - 3}` : ""}</span>
                    )}
                  </td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`btn-edit-user-${u.id}`} className="border-slate-700 text-slate-700"><Pencil size={14} /></Button>
                      <Button size="sm" variant="outline" onClick={() => openPw(u)} data-testid={`btn-password-user-${u.id}`} className="border-slate-700 text-slate-700"><KeyRound size={14} /></Button>
                      {u.id !== me?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" data-testid={`btn-delete-user-${u.id}`} className="border-slate-700 text-rose-400 hover:text-rose-300"><Trash2 size={14} /></Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#0E1524] border-slate-800 text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {u.username}?</AlertDialogTitle>
                              <AlertDialogDescription className="text-slate-400">They will lose access immediately.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="bg-slate-800 border-slate-700">Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(u.id)} className="bg-rose-500 hover:bg-rose-600">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="bg-[#0E1524] border-slate-800 text-white">
          <DialogHeader><DialogTitle className="font-heading">Reset password · {pwUser?.username}</DialogTitle></DialogHeader>
          <div>
            <Label className="label-caps !text-slate-400">New password</Label>
            <Input type="text" value={pwNew} onChange={e => setPwNew(e.target.value)} data-testid="input-new-password" className="mt-1.5 font-mono-tabular" placeholder="min 6 chars" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)} className="border-slate-700 text-slate-800">Cancel</Button>
            <Button onClick={savePw} data-testid="btn-save-password" className="bg-emerald-500 hover:bg-emerald-600 text-slate-950">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
